import { ReactNode, SyntheticEvent, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import cardValidator from 'card-validator';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { validationSchema, preferredSchema } from './validations';
import { Button, Checkbox, Input, Tooltip, Alert, RadioGroup, IRadioProps } from '@/shared/ui';
import { useAppSelector } from '@/shared/hooks/store';
import { selectUserProfileInfo } from '@/store/slices/onboarding/selectors';
import { creditCardIcons } from '@/shared/constants/main';
import { ManualAddressFormElements } from '@/shared/components/manual-address-form-elements';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { IPaymentFormData, paymentFormInitialBillingDetails, paymentFormInitialValues } from './constants';
import CircularProgress from '@mui/material/CircularProgress';
import Arrow from '@/assets/icons/arrow-payment.svg';

import CloseIcon from '@/assets/icons/x-icon-black.svg';
import HippacIcon from '@/assets/images/onboarding/hipaa.png';
import EncryptionIcon from '@/assets/images/onboarding/encryption.png';
import AddCard from "@/assets/images/add-card.png";
import CardShadow from '@/assets/icons/credit-card/card-shadow.svg';
import Lock from '@/assets/icons/lock.svg';
import LockDark from '@/assets/icons/lock-dark.svg';
import CvcIcon from '@/assets/icons/credit-card/cvc.svg';
import { AddCardExpectationDetails } from '../../../modules/onboarding/pages/review/components/payment';

import './style.css';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';
import { IInvoice } from '../../types/interfaces';

const buildDisplayCardNumber = (rawValue: string) => {
    let buildString = rawValue;
    const gaps = cardValidator.number(rawValue)?.card?.gaps;
    if (gaps) {
        buildString = addCardGapsBasedOnValidatedCard(rawValue, gaps);
    }

    return buildString;
};

const removeSpacesFromCardNumber = (cardNumber: string) => {
    return cardNumber.replace(/\s/g, '');
};

const addCardGapsBasedOnValidatedCard = (stringVal: string, gaps: Array<number>) => {
    const stringWithoutSpaces = stringVal.replace(/\s/g, '');
    const sections: string[] = [];
    for (let i = 0; i < gaps.length; i++) {
        if (i === 0) {
            sections.push(stringWithoutSpaces.slice(0, gaps[i]));
        } else {
            sections.push(stringWithoutSpaces.slice(gaps[i - 1], gaps[i]));
        }
    }
    sections.push(stringWithoutSpaces.slice(gaps[gaps.length - 1]));
    return sections.filter(section => !!section).join(' ');
};

const fields = [
    { name: 'firstName', queryClassName: 'payment__form_first_name' },
    { name: 'lastName', queryClassName: 'payment__form_last_name' },
    { name: 'cardNumber', queryClassName: 'payment__form_card_number' },
    { name: 'expirationDate', queryClassName: 'payment__form_expiration_date' },
    { name: 'cvv', queryClassName: 'payment__form_cvv' },
    { name: 'addressLine1', queryClassName: 'manual_address__form_elements__address' },
    { name: 'city', queryClassName: 'manual_address__form_elements__city' },
    { name: 'state', queryClassName: 'manual_address__form_elements__state' },
    { name: 'zip', queryClassName: 'manual_address__form_elements__zip' }
];

const InvoiceLineItem = ({ invoice }: { invoice: IInvoice }) => {
    const { invoiceNumber, totalDueAmount } = invoice;
    const invoiceNumberModified = invoiceNumber.split('QFE://')[1];

    return (
        <div className='flex justify-between font-dmSans text-16 leading-[150%] sm:text-18 sm:leading-[133%]'>
            <span>Invoice #{invoiceNumberModified}</span>
            <span>${totalDueAmount.toFixed(2)}</span>
        </div>
    )
}

const BalanceSummary = ({ unpaidInvoices }: { unpaidInvoices?: IInvoice[] }) => {
    const dueAmount = unpaidInvoices?.reduce((acc, invoice) => acc + (invoice.totalDueAmount || 0), 0) || 0;
    return (
        <div>
            <div className='font-dmSansBold text-20 leading-[120%] mb-[.9375rem]'>
                Balance Summary
            </div>
            <div className='flex flex-col gap-2 leading-[150%] font-dmSans text-16 leading-[150%]'>
                {unpaidInvoices?.map(invoice => <InvoiceLineItem key={invoice.id} invoice={invoice} />)}
            </div>
            <div className='flex flex-row justify-between text-16 leading-[150%] sm:text-18 sm:leading-[138%] mt-[15px]'>
                <span className='font-dmSansBold'>Subtotal</span>
                <span className='font-dmSansBold'>${dueAmount.toFixed(2)}</span>
            </div>
        </div>
    );
};

const Loading = () => {
    return (
        <CircularProgress
            className="!text-grey-500"
            data-testid="loading"
            sx={{
                position: 'absolute',
                width: '1.5rem !important',
                height: '1.5rem !important'
            }}
        />
    );
};

const PayMyBalancesButton = ({ text, onClick, testId, loading }: { text: string; onClick?: () => void; testId?: string; loading?: boolean }) => (
    <button
        className="relative flex justify-center items-center gap-[.625rem] px-[2.8125rem] py-[.9375rem] bg-aqua-500 rounded-[1.5rem] cursor-pointer"
        onClick={onClick}
        disabled={loading}
    >
        <div className={`flex items-center gap-[.625rem]`}>
            <span className="font-bold text-white text-18 leading-[105%]">{text}</span>
            <img src={Arrow} width={21} alt="Arrow icon" />
        </div>
        {loading && (
            <div className="absolute inset-0 flex justify-center items-center bg-mint-600 rounded-[1.5rem]">
                <Loading />
            </div>
        )}
    </button>
);

const CustomSubmitPaymentSectionWithPreferredSelection = ({ formik, addPaymentLoading, avoidIndividualErrorMessage, unpaidInvoices = [] }: { formik: ReturnType<typeof useFormikWrapper<IPaymentFormData>>; addPaymentLoading: boolean; avoidIndividualErrorMessage?: boolean; unpaidInvoices?: IInvoice[] }) => {
    const submitButton = useMemo(() => {
        return (unpaidInvoices?.length > 0 ? (
            <PayMyBalancesButton
                loading={addPaymentLoading}
                onClick={() => formik.handleSubmit()}
                testId="button-pay-my-balance"
                text={`Pay My Balance`}
            />
        ) : <Button
            onClick={() => formik.handleSubmit()}
            testId="add-payment-method-button"
            sx={{
                width: '100%',
                fontFamily: 'DM Sans medium'
            }}
            loading={addPaymentLoading}
            disabled={addPaymentLoading}
        >
            Add Card
        </Button>);
    }, [addPaymentLoading, formik, unpaidInvoices?.length]);

    return (
        <div className='flex flex-col gap-[1.25rem]'>
            {unpaidInvoices?.length > 0 && <BalanceSummary unpaidInvoices={unpaidInvoices} />}
            <div className={`flex flex-col bg-white p-5 gap-[1.25rem] rounded-2xl border-1 ${formik.errors.preferred ? 'border-red-500' : 'border-gray-300'}`}>
                <Checkbox
                    label={
                        <div className='flex flex-col justify-center items-start gap-[.4375rem]'>
                            <div className="">
                                <span className='font-dmSansBold font-bold text-18 leading-[133%]'>Authorize recurring payments</span>
                            </div>
                            <div className="font-dmSans text-16 leading-[150%]">
                                <span>Allow MedBox to securely save your payment method, charge it automatically each billing period, and <span className='font-dmSansBold font-bold'>set it as your default for future payments.</span></span>
                            </div>
                        </div>
                    }
                    className='recurring-payment-cb'
                    name="preferred"
                    checked={formik.values.preferred}
                    onChange={formik.handleChange}
                    error={!!formik.errors.preferred}
                    labelClassName={`${!formik.values.preferred ? 'checked' : ''}`}
                />
                {!!formik.errors.preferred && <Alert testId="payment-form-error-message" labelClassName="font-dmSans" label={'Please authorize recurring payments to proceed'} />}
            </div>
            {avoidIndividualErrorMessage && <Alert testId="payment-form-error-message" labelClassName="font-dmSans" label={'Please enter all card details to add payment'} />}
            {submitButton}
        </div>
    )
}

const FormLabel = ({ label, boldLabel, required, error }: { label: string; boldLabel: boolean; required?: boolean; error?: boolean }) => {
    return (
        <div className="flex flex-col justify-start items-start sm:flex-row sm:justify-between sm:items-center w-full">

            <div className="flex justify-start items-center gap-1">
                <span
                    className={`font-dmSans text-16 leading-[125%] ${boldLabel
                        ? "font-dmSansBold font-bold"
                        : "font-normal"
                        } ${error ? 'text-red-500' : ''}`}
                >
                    {label}
                </span>

                {required && (
                    <span className="font-bold text-red-500">*</span>
                )}
            </div>

        </div>
    );
};

const FormLabelCards = ({ boldLabel, required, error }: { boldLabel: boolean; required?: boolean; error?: boolean }) => (
    <div className='flex flex-col justify-start items-start sm:flex-row sm:justify-between sm:items-center w-full'>
        <div className='flex justify-start items-center gap-1'>
            <div>
                <img src={LockDark} alt="Lock Icon" height={12} />
            </div>
            <span
                className={`font-dmSans text-16 leading-[125%] ${boldLabel
                    ? "font-dmSansBold font-bold"
                    : "font-normal"
                    } ${error ? 'text-red-500' : ''}`}
            >
                Card Number
            </span>
            {required && <span className="font-bold text-red-500">*</span>}
        </div>
        <CardIcons />
    </div>
)


export const PaymentForm = ({
    onSave,
    addPaymentLoading,
    hideIconsFromInput = false,
    header,
    errorMessage,
    required = true,
    isPreferred = false,
    cancelAddPaymentHandler,
    cancelButtonText = 'Add Payment Later',
    headerOffset = 0,
    withinModal = true,
    className = '',
    onPaymentFormChange,
    preferredSelectedTrueRequired = false,
    onClose,
    unpaidInvoices,
    userAddress
}: {
    onSave?: (formData: IPaymentFormData) => void;
    addPaymentLoading: boolean;
    userAddress: string;
    hasFooter?: boolean;
    showCancel?: boolean;
    cancelAddPaymentHandler?: () => void;
    errorMessage?: string;
    cancelButtonText?: string;
    hideIconsFromInput?: boolean;
    header?: ReactNode;
    required?: boolean;
    saveButton?: ReactNode;
    withDefaultPayment?: boolean;
    isPreferred?: boolean;
    headerOffset?: number;
    withinModal?: boolean;
    className?: string;
    awarenessText?: string;
    onPaymentFormChange?: () => void;
    preferredSelectedTrueRequired?: boolean;
    onClose?: () => void;
    unpaidInvoices?: IInvoice[];
}) => {
    const ref = useRef(null);
    const theme = useTheme();
    const isDownXL = useMediaQuery(theme.breakpoints.down('xl'));
    const userProfile = useAppSelector(selectUserProfileInfo);
    const [showCVVPopover, setShowCVVPopover] = useState(false);

    const initialVals = {
        ...paymentFormInitialValues,
        preferred: isPreferred,
        hasDifferentBillingAddress: userAddress ? 1 : 2,
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
    };

    const formik = useFormikWrapper<IPaymentFormData>({
        initialValues: initialVals,
        validationSchema: preferredSelectedTrueRequired ? validationSchema.concat(preferredSchema) : validationSchema,
        onSubmit: values => {
            onSave?.(values);
        },
        onError: (errors) => {
            const errorFields = { ...errors, ...errors.billingDetails };
            const fieldWithError = fields.find(field => Object.keys(errorFields).includes(field.name));
            if (fieldWithError) {
                const element = document.querySelector(`.${fieldWithError.queryClassName}`);
                if (element) {
                    if (headerOffset && !withinModal) {
                        const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerOffset;
                        window.scrollTo({
                            top: elementPosition,
                            behavior: 'smooth'
                        });
                    } else {
                        document.querySelector(`.${fieldWithError.queryClassName}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            }
        },
    });

    useEffect(() => {
        onPaymentFormChange?.();
    }, [formik.values]);

    const handleBillingFormChange = useCallback(
        (field: string, value: string) => {
            formik.setFieldValue('billingDetails', { ...formik.values.billingDetails, [field]: value });
        },
        [formik]
    );

    const handleHasDifferentBillingAddress = useCallback((item: IRadioProps) => {
        formik.setFieldValue('hasDifferentBillingAddress', item.value);
        if (item.value === 2) {
            formik.setFieldValue('billingDetails', {
                ...paymentFormInitialBillingDetails
            });
        }
    }, [formik]);

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const { hasAnyInput, errorBanner } = useMemo(() => {
        const flattened = {
            ...(formik.errors.billingDetails ?? {}), // spread billingDetails if present
            ...Object.fromEntries(
                Object.entries(formik.errors).filter(([key]) => key !== "billingDetails")
            ),
        };

        // Check if any field in the form has input (non-empty value)

        const hasDiffBillingAddress = formik.values.hasDifferentBillingAddress === 2;
        const { billingDetails, preferred, hasDifferentBillingAddress, ...rest } = formik.values;
        let valsToCheckForInputs = { ...rest };
        if (hasDiffBillingAddress) {
            valsToCheckForInputs = { ...valsToCheckForInputs, ...billingDetails };
        }

        const totalErrorCount = Object.keys(flattened).length;


        return {
            hasAnyInput: totalErrorCount === 1,
            errorBanner: totalErrorCount > 1,
        };

    }, [formik]);

    return (
        <div className='flex flex-col gap-[1.25rem] items-center'>
            <div className='flex flex-col justify-center items-center text-center gap-[.625rem]'>
                <img width={120} height={120} src={AddCard} alt="" className="" />
                <span className='block font-agrandir text-26 sm:text-28 leading-[130%]'>
                    Add Card Details
                </span>
                <div className='flex flex-col font-dmSans leading-[133%] text-18 text-center'>
                    <div>
                        Keep your monthly refills coming
                    </div>
                    <div className='font-dmSansBold font-medium italic'>
                        A card on file is required
                    </div>
                </div>
            </div>
            <AddCardExpectationDetails label="Our Guarantee:" labelClassName="font-dmSansBold text-18 font-bold" />
            <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${className} border-1 border-gray-300 p-5 rounded-2xl`}>
                <div className="flex flex-row items-center gap-3">
                    <div className="h-[2.5rem] w-[2.5rem] flex-shrink-0 flex items-center justify-center rounded-full bg-green-300">
                        <img src={Lock} alt="Lock Icon" className="p-3" />
                    </div>
                    <span className="block font-dmSansBold text-18 font-bold">
                        SSL Encrypted & HIPAA Compliant
                    </span>
                </div>
                {/* <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:gap-5 lg:gap-7"> */}
                <Input
                    className="payment__form_first_name"
                    name="firstName"
                    label={
                        (error, boldLabel, required) => (
                            <FormLabel label='First Name' boldLabel={boldLabel} required={required} error={error} />
                        )
                    }
                    showErrorMessage={hasAnyInput}
                    value={formik.values?.firstName}
                    required
                    placeholder="ex. Jane"
                    error={formik.touched?.firstName && !!formik.errors?.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors?.firstName}
                    labelClassName="font-atkinsonHyperlegible"
                    testId="payment-form-first-name"
                />
                <Input
                    className="payment__form_last_name"
                    name="lastName"
                    label={
                        (error, boldLabel, required) => (
                            <FormLabel label="Last Name" boldLabel={boldLabel} required={required} error={error} />
                        )
                    }
                    showErrorMessage={hasAnyInput}
                    value={formik.values?.lastName}
                    required
                    placeholder="ex. Doh"
                    error={formik.touched?.lastName && !!formik.errors?.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors?.lastName}
                    labelClassName="font-atkinsonHyperlegible"
                    testId="payment-form-last-name"
                />
                <div className="relative">
                    <Input
                        className="payment__form_card_number"
                        name="cardNumber"
                        showErrorMessage={hasAnyInput}
                        label={
                            (error, boldLabel, required) => (<FormLabelCards boldLabel={boldLabel} required={required} error={error} />)
                        }
                        value={buildDisplayCardNumber(formik.values.cardNumber)}
                        required={required}
                        placeholder="ex. 1234 1234 1234 123"
                        error={formik.touched.cardNumber && !!formik.errors.cardNumber}
                        testId="payment-form-card-number"
                        onChange={e => {
                            const { value } = e.target;
                            const regex = /^[0-9\s]*$/;
                            if (!regex.test(e.target.value)) {
                                return;
                            }
                            const cardNumber = removeSpacesFromCardNumber(value);
                            const validator = cardValidator.number(cardNumber);
                            if (validator?.card?.type === 'american-express') {
                                if (cardNumber.length > 15) {
                                    return;
                                }
                            } else {
                                if (cardNumber.length > 16) {
                                    return;
                                }
                            }
                            e.target.value = cardNumber;
                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.cardNumber}
                        labelClassName="font-atkinsonHyperlegible w-full"
                        endContent={!hideIconsFromInput && !header && <CardIcons />}
                        startContent={<img src={CardShadow} />}
                    />
                </div>
                <div className="flex flex-col sm:flex-row gap-[.625rem]">
                    <Input
                        className="payment__form_expiration_date"
                        testId="payment-form-expiration-date"
                        name="expirationDate"
                        showErrorMessage={hasAnyInput}
                        label={
                            (error, boldLabel, required) => (
                                <FormLabel label="Expiration Date" boldLabel={boldLabel} required={required} error={error} />
                            )
                        }
                        value={formik.values.expirationDate}
                        required={required}
                        placeholder="ex. 05/26"
                        error={formik.touched.expirationDate && !!formik.errors.expirationDate}
                        errorMessage={formik.errors.expirationDate}
                        onBlur={formik.handleBlur}
                        onChange={e => {
                            const { value } = e.target;
                            const regex = /^(?:\d{2}\/\d{0,2}|\d{3}|\d{2}|\d)?$|^$/;
                            if (value.length <= 5 && regex.test(value)) {
                                if (value.length === 3 && !value.includes('/')) {
                                    e.target.value = value.slice(0, 2) + '/' + value.slice(2);
                                    formik.handleChange(e);
                                } else {
                                    formik.handleChange(e);
                                }
                            }
                        }}
                        labelClassName="font-atkinsonHyperlegible"
                    />
                    <Input
                        className="payment__form_cvv"
                        testId="payment-form-cvv"
                        name="cvv"
                        showErrorMessage={hasAnyInput}
                        label={
                            (error, boldLabel, required) => (
                                <FormLabel label="CVV/CVC" boldLabel={boldLabel} required={required} error={error} />
                            )
                        }
                        value={formik.values.cvv}
                        required={required}
                        placeholder="ex. 456"
                        error={formik.touched.cvv && !!formik.errors.cvv}
                        onChange={e => {
                            const { value } = e.target;
                            const regex = /^\d*$/;
                            if (value.length <= 4 && regex.test(value)) {
                                formik.handleChange(e);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.cvv}
                        labelClassName="font-atkinsonHyperlegible overflow-hidden"
                        endContent={<img src={CvcIcon} className="" />}
                    />
                </div>
                <div className='w-full flex'>
                    <a
                        ref={ref}
                        onClick={() => setShowCVVPopover(true)}
                        className="ml-auto cursor-pointer font-atkinsonHyperlegible text-[.875rem] text-primary underline md:text-lg"
                    >
                        What is CVV/CVC?
                    </a>
                </div>

                <div className="w-full border-b-[.0625rem] border-grey-200 mb-[.625rem]" />
                {userAddress &&
                    <RadioGroup
                        formControl={false}
                        value={formik?.values.hasDifferentBillingAddress}
                        onOptionClick={handleHasDifferentBillingAddress}
                        controlled={true}
                        fullViewClickable={true}
                        items={[
                            {
                                label: <span>Billing address is same as shipping address <span className='ph-no-capture font-dmSansBold'>{`(${userAddress})`}</span></span>,
                                value: 1
                            },
                            {
                                label: 'I have a different billing address for this card',
                                value: 2
                            },
                        ]}
                        name="payment-options"
                        buttonClassName="billing-options-radio"
                        dataTestId="payment-options"
                        optionType="radio"
                    />
                }
                {formik.values.hasDifferentBillingAddress === 2 ? (
                    <>
                        <ManualAddressFormElements
                            handleChange={handleBillingFormChange}
                            values={formik.values.billingDetails || paymentFormInitialBillingDetails}
                            errors={formik.errors.billingDetails || {}}
                            touched={formik.touched.billingDetails || {}}
                            showApartmentSuiteBoxNumberField
                            formTitle="Billing Address"
                            formTitleClassName="font-atkinsonBold !text-xl mb-0 mt-1"
                            avoidIndividualErrorMessage={!hasAnyInput}
                        />
                    </>
                ) : null}
                {errorMessage && <Alert testId="payment-form-error-message" labelClassName="font-dmSans" label={errorMessage} />}
                <CustomSubmitPaymentSectionWithPreferredSelection avoidIndividualErrorMessage={errorBanner} formik={formik} addPaymentLoading={addPaymentLoading} unpaidInvoices={unpaidInvoices} />
                {cancelAddPaymentHandler && (
                    <Button
                        testId="button-cancel-payment"
                        onClick={cancelAddPaymentHandler}
                        sx={{
                            width: '100%',
                            [theme.breakpoints.down('md')]: {
                                mt: '.625rem'
                            },
                            fontFamily: 'DM Sans medium'
                        }}
                        theme="mintNoHoverReaction"
                        variant="outlined"
                        disabled={addPaymentLoading}
                    >
                        {cancelButtonText}
                    </Button>
                )}

            </form>
            {onClose && (
                <Button
                    onClick={onClose}
                    variant="text"
                >
                    <span className="underline font-dmSansBold font-bold text-17 text-primary">Cancel</span>
                </Button>
            )}
            <div className="hidden flex-wrap items-center justify-center gap-3 lg:flex lg:gap-4">
                <img src={HippacIcon} alt="" className="h-8 max-w-36 sm:h-auto" />
                <img src={EncryptionIcon} alt="" className="h-8 max-w-36 sm:h-auto" />
            </div>
            <Tooltip
                customPosition={isDownXL ? 'bottom-left' : 'top-right'}
                wrapperClassName=""
                hasCloseIcon={false}
                open={showCVVPopover}
                anchorEl={ref.current}
                squareBorderColor='!border-white'
                squareColor='!bg-white'
                className='!bg-white'
                onClose={() => setShowCVVPopover(false)}
            >
                <div
                    onClick={() => setShowCVVPopover(false)}
                    className="p-[.3125rem] cursor-pointer min-w-[19px] h-[19px] flex justify-center items-center bg-mint-400 rounded-full"
                >
                    <img src={CloseIcon} alt="" width={10} className="w-full" />
                </div>
                <div className="text-xs ml-[9px] font-atkinsonHyperlegible">
                    <span className="font-atkinsonBold">CVV/CVC</span>
                    &nbsp;
                    number is the last three digits at the back of your card. For American Express cards, the CVV is a 4-digit number on the front of the card.
                </div>
            </Tooltip>
        </div>
    );
};

const CardIcons = () => {
    return (
        <div className="flex gap-[.125rem]">
            {Object.values(creditCardIcons).map((icon, idx) => (
                <img src={icon} key={idx} className="w-[2.0625rem] h-[1.1875rem] w-full" />
            ))}
        </div>
    );
};

export default withScrollRestoration(PaymentForm);
